/**
 * The Hangar station panel (M6 spec §4, plan P27): the owned ships, docked-here first, with switch, ferry, transfer
 * and insure; M9 (CH-12b) adds a claimed hull's "claimed" flag, its flaws with a *Fix* each, its First Rush edge and
 * value, and the two *Scrap* forms. PF-2: the open hulk claims, each with *Abandon claim* (no trip to the hulk). Thin: the rules live in `models/hangar.ts`; the actions come from the panel.
 * M10 P5a3c: each row lists its last three hull events, and, with `onRename`, a *Rename* button (the last of the row's
 * ship actions) that opens a name field in place; the same button then reads *Save* (Escape in the field cancels). The worker's
 * refusals (`badName`, `nameTaken`) come back in the status line.
 */
import { useState, type ReactNode } from 'react'
import { SHIP_NAME_MAX_CHARS } from '../../career/types'
import { cleanShipName } from '../../fleet-link/hangar'
import { keepTyping } from '../corp/typing'
import { FocusButton } from '../focus'
import { HANGAR_TEXT, type HangarAction, type HangarActionId, type HangarActionKind, type HangarModel, type HangarRow } from './models/hangar'
import '../strategic/strategic.css'
import './stationPanels.css'

export interface HangarProps {
  model: HangarModel
  status: string | null
  busy: boolean
  /** `flaw` is the flaw's list index for `fixFlaw`. */
  onAction: (shipId: string, action: HangarActionKind, flaw?: number) => void
  /** PF-2: abandons the open hulk claim `key` (`FleetService.closeHulkClaim`). Absent: no claims section. */
  onAbandon?: (key: string) => void
  /** M10 P5a3c: renames owned ship `shipId` (`FleetService.renameShip`). Absent: no Rename button. */
  onRename?: (shipId: string, name: string) => void
}

/** M10 P5a3c: the name sent for a typed rename: trimmed when valid, else as typed (the worker refuses it `badName`). */
export function renameValue(typed: string): string {
  return cleanShipName(typed) ?? typed
}

const HERE_ACTIONS: readonly HangarActionId[] = ['switch', 'insure']
const AWAY_ACTIONS: readonly HangarActionId[] = ['ferry', 'transfer', 'insure']

/** One button of a row: the action and, for a fix, the flaw it fixes. */
interface RowButton {
  key: string
  action: HangarAction
  testId: string
  flaw?: number
}

/** A row's buttons in focus order: its ship actions, the scrap forms, then one fix per flaw. */
function rowButtons(r: HangarRow, ids: readonly HangarActionId[]): RowButton[] {
  const out: RowButton[] = ids.map((id) => ({ key: id, action: r.actions[id], testId: `hangar-${id}-${r.id}` }))
  if (r.scrap !== null) {
    out.push({ key: 'scrapCredits', action: r.scrap.credits, testId: `hangar-scrapCredits-${r.id}` })
    out.push({ key: 'scrapGoods', action: r.scrap.goods, testId: `hangar-scrapGoods-${r.id}` })
  }
  for (const f of r.flaws) out.push({ key: `fix-${f.index}`, action: f.fix, testId: `hangar-fixFlaw-${r.id}-${f.index}`, flaw: f.index })
  return out
}

function Button(p: { b: RowButton; shipId: string; index: number; busy: boolean; onAction: HangarProps['onAction'] }): ReactNode {
  const a = p.b.action
  return (
    <FocusButton
      group="hangar"
      index={p.index}
      className="ui-btn--small"
      testId={p.b.testId}
      disabled={!a.enabled || p.busy}
      title={a.reason === '' ? undefined : a.reason}
      onActivate={() => p.onAction(p.shipId, a.id, p.b.flaw)}
    >
      {a.label}
    </FocusButton>
  )
}

/** M10 P5a3c: a row's focus stops: its buttons and, with a rename, the Rename/Save button. */
function rowStops(r: HangarRow, ids: readonly HangarActionId[], rename: boolean): number {
  return rowButtons(r, ids).length + (rename ? 1 : 0)
}

function Rename(p: { row: HangarRow; index: number; busy: boolean; onRename: NonNullable<HangarProps['onRename']> }): ReactNode {
  const r = p.row
  const [typed, setTyped] = useState<string | null>(null)
  const editing = typed !== null
  return (
    <>
      {editing ? (
        <input
          type="text"
          maxLength={SHIP_NAME_MAX_CHARS}
          value={typed}
          aria-label={`New name for the ${r.name}`}
          disabled={p.busy}
          data-testid={`hangar-rename-input-${r.id}`}
          onKeyDown={(e) => {
            // Escape cancels the rename only (the screen stays open); every other key stays in the field.
            if (e.key === 'Escape') {
              e.stopPropagation()
              setTyped(null)
              return
            }
            keepTyping(e)
          }}
          onChange={(e) => setTyped(e.target.value)}
        />
      ) : null}
      <FocusButton
        group="hangar"
        index={p.index}
        className="ui-btn--small"
        testId={editing ? `hangar-rename-save-${r.id}` : `hangar-rename-${r.id}`}
        disabled={p.busy}
        onActivate={() => {
          if (!editing) {
            setTyped(r.name)
            return
          }
          setTyped(null)
          if (typed !== r.name) p.onRename(r.id, renameValue(typed))
        }}
      >
        {editing ? 'Save' : 'Rename'}
      </FocusButton>
    </>
  )
}

function Row(p: { row: HangarRow; ids: readonly HangarActionId[]; first: number; busy: boolean; onAction: HangarProps['onAction']; onRename?: HangarProps['onRename'] }): ReactNode {
  const r = p.row
  const buttons = rowButtons(r, p.ids)
  const main = buttons.filter((b) => b.flaw === undefined)
  const fixes = buttons.filter((b) => b.flaw !== undefined)
  const renameStops = p.onRename === undefined ? 0 : 1
  const reasons = main.map((b) => b.action).filter((a) => !a.enabled && a.reason !== '')
  return (
    <div className="fp-row" data-testid={`hangar-row-${r.id}`}>
      <div>
        <div className="fp-row__name">
          {r.name} <span className="fp-row__sub">{r.classLabel}</span>
          {r.claimed ? (
            <span className="fp-row__sub" data-testid={`hangar-claimed-${r.id}`}>
              {` · ${HANGAR_TEXT.claimed}`}
            </span>
          ) : null}
          {r.flown ? <span className="fp-row__sub"> · flying</span> : null}
        </div>
        <div className="fp-row__sub">
          {r.status} · {r.where} · {r.captain} · {r.insured ? 'Insured' : 'Uninsured'}
          {r.registered ? null : ` · ${HANGAR_TEXT.unregisteredStatus}`}
        </div>
        {r.value !== null ? <div className="fp-row__sub">{r.value}</div> : null}
        {r.edge !== null ? <div className="fp-row__sub">{r.edge}</div> : null}
        {r.history.length > 0 ? (
          <ul className="fp-row__history" data-testid={`hangar-history-${r.id}`}>
            {r.history.map((h, k) => (
              <li key={k} className="fp-row__sub">
                {h}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div />
      {reasons.length > 0 ? <div className="fp-row__reason">{reasons.map((a) => `${a.label}: ${a.reason}`).join(' ')}</div> : null}
      <div className="fp-row__actions">
        {main.map((b, k) => (
          <Button key={b.key} b={b} shipId={r.id} index={p.first + k} busy={p.busy} onAction={p.onAction} />
        ))}
        {p.onRename !== undefined ? <Rename row={r} index={p.first + main.length} busy={p.busy} onRename={p.onRename} /> : null}
      </div>
      {r.flaws.length > 0 ? (
        <ul className="fp-row__flaws" data-testid={`hangar-flaws-${r.id}`}>
          {r.flaws.map((f, k) => (
            <li key={f.index} className="fp-row__sub">
              {f.text} <Button b={fixes[k]} shipId={r.id} index={p.first + main.length + renameStops + k} busy={p.busy} onAction={p.onAction} />
              {!f.fix.enabled && f.fix.reason !== '' ? <span className="fp-row__reason"> {f.fix.reason}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function Hangar(p: HangarProps): ReactNode {
  const m = p.model
  let next = 0
  const rows = (list: readonly HangarRow[], ids: readonly HangarActionId[]): ReactNode[] =>
    list.map((r) => {
      const first = next
      next += rowStops(r, ids, p.onRename !== undefined)
      return <Row key={r.id} row={r} ids={ids} first={first} busy={p.busy} onAction={p.onAction} onRename={p.onRename} />
    })
  return (
    <div className="sui fp-panel" data-testid="fleet-hangar">
      <header className="sui-head">
        <h2 className="sui-title">Hangar</h2>
        <p className="sui-sub">{m.transferNote}</p>
      </header>
      {m.leased !== null ? (
        <section className="sui-section" data-testid="hangar-leased">
          <h3 className="sui-section__title">{m.leasedHeader}</h3>
          <p className="sui-line">{m.leased}</p>
        </section>
      ) : null}
      {m.empty !== null ? <p className="sui-empty">{m.empty}</p> : null}
      {m.empty === null ? (
        <section className="sui-section">
          <h3 className="sui-section__title">Docked here</h3>
          {m.here.length === 0 ? <p className="sui-empty">None of your ships is docked here.</p> : rows(m.here, HERE_ACTIONS)}
        </section>
      ) : null}
      {m.away.length > 0 ? (
        <section className="sui-section">
          <h3 className="sui-section__title">Elsewhere</h3>
          {rows(m.away, AWAY_ACTIONS)}
        </section>
      ) : null}
      {m.claims.length > 0 && p.onAbandon !== undefined ? (
        <section className="sui-section" data-testid="hangar-claims">
          <h3 className="sui-section__title">{m.claimsHeader}</h3>
          {m.claims.map((c, k) => {
            const onAbandon = p.onAbandon
            return (
              <div className="fp-row" key={c.key} data-testid={`hangar-claim-${c.key}`}>
                <div className="fp-row__name">{c.text}</div>
                <div />
                <div className="fp-row__actions">
                  <FocusButton
                    group="hangar"
                    index={next + k}
                    className="ui-btn--small"
                    testId={`hangar-abandonClaim-${c.key}`}
                    disabled={!c.abandon.enabled || p.busy}
                    onActivate={() => onAbandon?.(c.key)}
                  >
                    {c.abandon.label}
                  </FocusButton>
                </div>
              </div>
            )
          })}
        </section>
      ) : null}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="hangar-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
